import { Button } from '@/components/Elements/Button/Button';
import { Box, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { ResetPasswordFormType } from '@/features/reservation/types/ResetPasswordFormType';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { confirmResetPassword } from 'aws-amplify/auth';
import { PageName } from '.';
import { useRef, useState } from 'react';

const schema: yup.ObjectSchema<ResetPasswordFormType> = yup.object({
  confirmationCode: yup.string().required('コードを入力してください。'),
  newPassword: yup
    .string()
    .required('パスワードを入力してください。')
    .test('password-complexity', 'パスワードの複雑さが不十分です', function (value) {
      const errors = passwordValidation(value);
      if (errors) {
        return this.createError({ message: errors.join('') });
      }
      return true;
    }),
  newPasswordConfirm: yup
    .string()
    .oneOf([yup.ref('newPassword'), undefined], '同じパスワードを入力してください。')
    .required('パスワードを入力してください。')
});

const passwordValidation = (value: string) => {
  const errors = [];

  // 8文字以上のチェック
  if (value.length < 8) {
    errors.push('パスワードは8文字以上を入力してください。');
  }
  if (!/[a-z]/.test(value)) {
    errors.push('小文字を1文字以上含めてください。');
  }
  if (!/[A-Z]/.test(value)) {
    errors.push('大文字を1文字以上含めてください。');
  }
  if (!/\d/.test(value)) {
    errors.push('数字を1文字以上含めてください。');
  }
  if (!/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(value)) {
    errors.push('記号(^ $ * . [ ] { } ( ) ? - " ! @ # % & /  , > < \' : ; | _ ~ ` + =)を1文字以上含めてください。');
  }
  return errors.length ? errors : null;
};

export const ResetPasswordForm = (props: {
  setActivePage: React.Dispatch<React.SetStateAction<PageName>>;
  userName: string;
}) => {
  let tokenFormRef = useRef<HTMLInputElement>(null);
  const [hasError, setError] = useState(false);

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors }
  } = useForm<ResetPasswordFormType>({
    defaultValues: {
      confirmationCode: '',
      newPassword: '',
      newPasswordConfirm: ''
    },
    resolver: yupResolver(schema)
  });

  const onSubmit = async () => {
    const confirmationCode = tokenFormRef.current?.value.trim() || '';
    const newPassword = getValues('newPassword');

    try {
      await confirmResetPassword({
        username: props.userName,
        newPassword,
        confirmationCode
      });
      setError(false);
      props.setActivePage(PageName.ResetPasswordSuccess);
    } catch (error) {
      setError(true);
      console.error(error);
    }
  };

  const ErrorMessage = (props: { message: string | undefined }) => {
    if (!props.message) return null;
    return (
      <Typography color="error" style={{ fontSize: '12px', marginLeft: '4px' }}>
        {props.message}
      </Typography>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography className="font-bold" variant="h5" align="center" sx={{ mt: 4, mb: 3 }}>
        メールを確認してください
      </Typography>
      <Typography variant="body1" align="center" color="textSecondary" mb={4}>
        パスワードをリセットするには、
        <br />
        あなたのメールアドレスに送信された
        <br />
        コードを入力してください。
      </Typography>

      <Grid container direction={'row'} justifyContent={'center'} spacing={2}>
        <Grid item xs={12} sm={8}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack direction={'column'}>
              {/* コード入力フィールド */}
              <Grid item xs={12}>
                <Stack direction={'column'}>
                  <InputLabel className="font-bold">コード</InputLabel>
                  <Controller
                    name="confirmationCode"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <TextField
                          inputRef={tokenFormRef}
                          {...field}
                          placeholder="メールアドレスに送信されたコードを入力してください"
                          fullWidth
                          size="small"
                          sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                          error={!!error}
                        />
                        {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                      </>
                    )}
                  />
                </Stack>
              </Grid>
              {/* パスワード */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Stack direction={'column'}>
                  <InputLabel className="font-bold">新しいパスワード</InputLabel>
                  <Controller
                    name="newPassword"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <TextField
                          {...field}
                          placeholder="8文字以上（大文字・小文字・数字・記号を含む）"
                          type="password"
                          fullWidth
                          size="small"
                          sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                          error={!!error}
                        />
                        {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                      </>
                    )}
                  />
                </Stack>
              </Grid>
              {/* パスワード確かめ */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Stack direction={'column'}>
                  <InputLabel className="font-bold">新しいパスワード（確認）</InputLabel>
                  <Controller
                    name="newPasswordConfirm"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <TextField
                          {...field}
                          placeholder="8文字以上（大文字・小文字・数字・記号を含む）"
                          type="password"
                          fullWidth
                          size="small"
                          sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                          error={!!error}
                        />
                        {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                      </>
                    )}
                  />
                </Stack>
              </Grid>
            </Stack>
            <Grid item sx={{ mt: 2 }}>
              <Button type="submit" variant="main" size="lg" className="w-full">
                パスワードをリセットする
              </Button>
            </Grid>
            {/* エラーメッセージを表示 */}
            {hasError && (
              <Typography color="error" align="center" sx={{ mt: 2 }}>
                認証コードが正しくありません。再度入力してください。
              </Typography>
            )}
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};
